import { useState } from "react";
import { useAccountContext, cognitoUserAttributes } from "../../../contexts/index";
import { drainQueue } from "../../../utils/OfflineDrain";

interface ImpersonationPanelProps {
  clients: Array<{ UserId: string; Email: string }>;
}

export function ImpersonationPanel({ clients }: ImpersonationPanelProps) {
  const {
    userCognitoAttrubutes,
    impersonatedUserId,
    impersonatedUserEmail,
    startImpersonation,
    stopImpersonation,
  } = useAccountContext();

  const [selectedUserId, setSelectedUserId] = useState<string>(impersonatedUserId || "");
  const [isStartHovered, setIsStartHovered] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const isSelectionInvalid =
    !selectedUserId || selectedUserId === (userCognitoAttrubutes as cognitoUserAttributes).sub;

  const handleStartImpersonation = async () => {
    if (selectedUserId && selectedUserId !== (userCognitoAttrubutes as cognitoUserAttributes).sub) {
      const selectedEmail = clients.find((client) => client.UserId === selectedUserId)?.Email;
      const confirmMessage = `Are you sure you want to impersonate ${selectedEmail ?? selectedUserId}?\n\nThis will reload the page and you will see their data.`;
      if (window.confirm(confirmMessage)) {
        setIsStarting(true);
        // Flush any of the admin's own queued offline saves first — offline queueing is
        // disabled for the whole impersonation session (see OfflineDrain.ts), so anything
        // still unsynced after this point just waits until impersonation ends. Best-effort:
        // a stuck item shouldn't block starting impersonation.
        try {
          await drainQueue();
        } catch {
          // ignore — proceed regardless, see comment above
        }
        startImpersonation(selectedUserId, selectedEmail);
        // Reload to fetch the impersonated user's data
        window.location.reload();
      }
    }
  };

  const handleStopImpersonation = () => {
    if (window.confirm("Stop impersonating and return to your account?")) {
      stopImpersonation();
      // Reload to fetch your own data
      window.location.reload();
    }
  };

  return (
    <div style={{ marginTop: "1rem", padding: "1rem", border: "2px solid #608cdf", borderRadius: "8px", backgroundColor: "#f0f7ff" }}>
      <h4 style={{ color: "#608cdf", marginTop: 0, textAlign: "center", textDecoration: "underline" }}>User Impersonation</h4>

      {impersonatedUserId && (
        <div style={{
          backgroundColor: "#ff6b6b",
          color: "white",
          padding: "0.5rem",
          borderRadius: "4px",
          marginBottom: "1rem",
          fontWeight: "bold"
        }}>
          ⚠️ Currently impersonating user: {impersonatedUserEmail ?? impersonatedUserId}
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", fontSize: "1rem", color: "#608cdf" }}>
          Select User to Impersonate:
        </label>
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #608cdf" }}
        >
          <option value="" disabled>
            Select User
          </option>
          {clients.map((client) => (
            <option key={client.UserId} value={client.UserId}>
              {client.Email}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={handleStartImpersonation}
          disabled={isSelectionInvalid || isStarting}
          onMouseEnter={() => setIsStartHovered(true)}
          onMouseLeave={() => setIsStartHovered(false)}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: (isSelectionInvalid || isStarting) ? "#e0e0e0" : (isStartHovered ? "#608cdf" : "#fff"),
            color: (isSelectionInvalid || isStarting) ? "#999" : (isStartHovered ? "#fff" : "#608cdf"),
            border: (isSelectionInvalid || isStarting) ? "1px solid #ccc" : "1px solid #608cdf",
            borderRadius: "4px",
            cursor: (isSelectionInvalid || isStarting) ? "not-allowed" : "pointer",
            fontWeight: "600"
          }}
        >
          {isStarting ? "Syncing your data..." : "Start Impersonation"}
        </button>

        {impersonatedUserId && (
          <button
            onClick={handleStopImpersonation}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#ff6b6b",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600"
            }}
          >
            Stop Impersonation
          </button>
        )}
      </div>

      <div style={{ marginTop: "0.75rem", fontSize: "0.85rem", color: "#666" }}>
        <strong>Note:</strong> Impersonation will reload the page and persist until you stop it or logout.
      </div>
    </div>
  );
}
